import { Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BoltIcon
} from '@heroicons/react/20/solid';
import { useAgency } from '@/contexts/AgencyContext';

// Plan badge colors
const PLAN_COLORS = {
  starter: 'bg-white/[0.04] text-white/80 border-white/[0.06]',
  growth: 'bg-blue-100 text-blue-700 border-blue-200',
  enterprise: 'bg-purple-100 text-purple-700 border-purple-200'
};

// Plan icons
const PLAN_ICONS = {
  starter: BoltIcon,
  growth: RocketLaunchIcon,
  enterprise: SparklesIcon
};

export function AgencySelector() {
  const { selectedAgency, agencies, isLoading, selectAgency } = useAgency();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter agencies based on search
  const filteredAgencies = agencies.filter(agency =>
    searchQuery === '' ||
    agency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agency.plan.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-white/60">
        <BuildingOffice2Icon className="h-5 w-5 animate-pulse" />
        <span>Loading agencies...</span>
      </div>
    );
  }

  if (agencies.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-white/60">
        <BuildingOffice2Icon className="h-5 w-5" />
        <span>No agencies available</span>
      </div>
    );
  }

  return (
    <Listbox value={selectedAgency?.id || ''} onChange={selectAgency}>
      <div className="relative">
        <Listbox.Button className="relative w-64 cursor-pointer rounded-lg bg-white/[0.04]  py-2.5 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-white/[0.08] hover:ring-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6 transition-all">
          <span className="flex items-center gap-2">
            {selectedAgency ? (
              <>
                <BuildingOffice2Icon className="h-5 w-5 text-white/70 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="block truncate font-semibold text-white">{selectedAgency.name}</span>
                  <span className="block text-xs text-white/60 truncate capitalize">{selectedAgency.plan} Plan</span>
                </div>
              </>
            ) : (
              <>
                <BuildingOffice2Icon className="h-5 w-5 text-white/50" />
                <span className="block truncate text-white/70 font-medium">Select Agency</span>
              </>
            )}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-white/50" aria-hidden="true" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-50 mt-2 w-96 overflow-hidden rounded-lg bg-gray-900 text-base shadow-2xl ring-1 ring-white/20 focus:outline-none sm:text-sm">
            {/* Search bar */}
            {agencies.length > 5 && (
              <div className="p-2 border-b border-white/10">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/50" />
                  <input
                    type="text"
                    placeholder="Search agencies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-gray-800 border border-white/[0.06] rounded-md text-white placeholder-white/50 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            {/* Header */}
            <div className="px-3 py-2  border-b border-white/[0.06]">
              <span className="text-xs font-semibold text-white/60 uppercase tracking-wide">
                Agencies {searchQuery && `(${filteredAgencies.length} results)`}
              </span>
            </div>

            <div className="max-h-80 overflow-y-auto">
              <div className="py-1">
                {filteredAgencies.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-white/60">
                    No agencies found matching "{searchQuery}"
                  </div>
                ) : (
                  filteredAgencies.map((agency) => {
                    const PlanIcon = PLAN_ICONS[agency.plan as keyof typeof PLAN_ICONS] || BoltIcon;
                    const planColorClass = PLAN_COLORS[agency.plan as keyof typeof PLAN_COLORS] || PLAN_COLORS.starter;

                    return (
                      <Listbox.Option
                        key={agency.id}
                        value={agency.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-3 pl-3 pr-9 ${
                            active ? 'bg-white/[0.04]' : ''
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                <BuildingOffice2Icon className="h-5 w-5 text-white/70" />
                              </div>

                              <div className="flex-1 min-w-0">
                                {/* Agency name */}
                                <div className="flex items-center gap-2">
                                  <span className={`block truncate ${selected ? 'font-semibold text-white' : 'font-medium text-white/80'}`}>
                                    {agency.name}
                                  </span>
                                  {agency.status === 'active' && (
                                    <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                      Active
                                    </span>
                                  )}
                                </div>

                                {/* Plan badge */}
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${planColorClass}`}>
                                    <PlanIcon className="h-3 w-3" />
                                    <span className="capitalize">{agency.plan}</span>
                                  </span>
                                  {agency._count && (
                                    <>
                                      <span className="text-xs text-white/50">•</span>
                                      <span className="text-xs text-white/60">
                                        {agency._count.brands}/{agency.maxBrands} brands
                                      </span>
                                      <span className="text-xs text-white/50">•</span>
                                      <span className="text-xs text-white/60">
                                        {agency._count.users}/{agency.maxUsers} users
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-600">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    );
                  })
                )}
              </div>
            </div>

            {/* Footer info */}
            <div className="px-3 py-2  border-t border-white/[0.06]">
              <div className="text-xs text-white/60">
                {agencies.length} {agencies.length === 1 ? 'agency' : 'agencies'} total
              </div>
            </div>
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
